import React, { useState, useEffect } from 'react'

import { useInView } from 'react-cool-inview'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, oneOf, object, bool } from 'prop-types'

import { useIs3dPrintModel } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'

import { convertToEmbedUrl } from '../../Utils/Video'

const EMBED_SOURCES = ['youtube', 'vimeo', 'tiktok', 'x']

function MediaRenderer({
  url,
  fallbackUrl = null,
  file = null,
  type,
  classPrefix = 'media-renderer',
  containerClass = '',
  externalId = null,
  source = null,
  isGalleryView = false,
}) {
  const is3dPrintModel = useIs3dPrintModel()
  const [blobUrl, setBlobUrl] = useState(null)
  const [hasError, setHasError] = useState(false)
  const [currentUrl, setCurrentUrl] = useState(url)

  const { observe, inView } = useInView({
    rootMargin: '50px',
    unobserveOnEnter: true,
  })

  const isUploadedFile = Boolean(file)

  const embedSource = (() => {
    if (source && EMBED_SOURCES.includes(source)) {
      return source
    }

    if (!currentUrl) {
      return null
    }

    if (currentUrl.includes('tiktok')) return 'tiktok'
    if (currentUrl.includes('youtube') || currentUrl.includes('youtu.be')) return 'youtube'
    if (currentUrl.includes('vimeo')) return 'vimeo'
    if (currentUrl.includes('twitter') || currentUrl.includes('x.com')) return 'x'

    return null
  })()

  const isEmbedVideo = !file && type === 'video' && Boolean(embedSource)

  useEffect(() => {
    setCurrentUrl(url)
    setHasError(false)
  }, [url])

  useEffect(() => {
    if (file && (['image', 'video'].includes(type))) {
      const createdBlobUrl = URL.createObjectURL(file)
      setBlobUrl(createdBlobUrl)

      return () => {
        URL.revokeObjectURL(createdBlobUrl)
      }
    }

    setBlobUrl(null)
  }, [file, type])

  const displayUrl = blobUrl || currentUrl
  const isBlobUrl = displayUrl && displayUrl.startsWith('blob:')

  const handleImageError = () => {
    if (fallbackUrl && currentUrl !== fallbackUrl) {
      setCurrentUrl(fallbackUrl)
      setHasError(false)
    } else {
      setHasError(true)
    }
  }

  const handleVideoError = () => {
    if (fallbackUrl && currentUrl !== fallbackUrl) {
      setCurrentUrl(fallbackUrl)
      setHasError(false)
    } else {
      setHasError(true)
    }
  }

  const renderVideoContent = () => {
    if (isUploadedFile || (!isEmbedVideo && type === 'video')) {
      return (
        <video
          src={displayUrl}
          className={`${classPrefix}__video`}
          controls
          muted
          onError={handleVideoError}
          onLoadedData={() => setHasError(false)}
          aria-label={is3dPrintModel ? '3D Make video' : 'Use case video'}
        >
          <track kind="captions" src="" srcLang="en" label="English captions" />
        </video>
      )
    }

    if (isEmbedVideo) {
      const isDragging = containerClass && containerClass.includes('dragging')
      const isTikTok = embedSource === 'tiktok'
      const isX = embedSource === 'x'

      const iframeClasses = [
        `${classPrefix}__embed-iframe`,
        isDragging ? `${classPrefix}__embed-iframe--dragging` : '',
      ].filter(Boolean).join(' ')

      // TikTok embeds require specific iframe attributes per TikTok Embed Player API
      // Reference: https://developers.tiktok.com/doc/embed-player
      const iframeSandbox = isTikTok
        ? "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        : "allow-scripts allow-same-origin allow-presentation"

      const iframeAllow = isTikTok
        ? "fullscreen"
        : "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"

      const xEmbedId = `twitter-widget-${externalId}`
      const src = convertToEmbedUrl(displayUrl, externalId, embedSource || source, xEmbedId)

      if (isX) {
        return (
          <iframe
            id={xEmbedId}
            className={classnames(
              `${classPrefix}__x-embed`,
              isGalleryView && `${classPrefix}__x-embed--gallery-view`,
            )}
            allowFullScreen
            title="X Post"
            src={src}
            data-tweet-id={externalId}
            scrolling="no"
          />
        )
      }

      return (
        <div className={`${classPrefix}__embed`}>
          <iframe
            src={src}
            className={iframeClasses}
            sandbox={iframeSandbox}
            allow={iframeAllow}
            allowFullScreen
            title={is3dPrintModel ? '3D Make video embed' : 'Use case video embed'}
            onError={handleVideoError}
          />
        </div>
      )
    }

    return (
      <div className={`${classPrefix}__placeholder`}>
        <div className={`${classPrefix}__placeholder-icon`} />
      </div>
    )
  }

  const renderMediaContent = () => {
    if (hasError && isBlobUrl && isUploadedFile) {
      return (
        <div className={`${classPrefix}__placeholder processing`}>
          <div className={`${classPrefix}__placeholder-icon`} />
          <div className={classnames('cgt-legacy-text-tiny', `${classPrefix}__placeholder-text`)}>
            Processing...
          </div>
        </div>
      )
    }

    if (type === 'image') {
      return (
        <img
          src={displayUrl}
          alt={is3dPrintModel ? '3D Make' : 'Use case'}
          className={`${classPrefix}__image`}
          onError={handleImageError}
          onLoad={() => setHasError(false)}
        />
      )
    }

    if (type === 'video') {
      return renderVideoContent()
    }

    return (
      <div className={`${classPrefix}__placeholder`}>
        <div className={`${classPrefix}__placeholder-icon`} />
      </div>
    )
  }

  const combinedClassName = [classPrefix, containerClass].filter(Boolean).join(' ')

  return (
    <div className={combinedClassName} ref={observe}>
      {inView ? (
        renderMediaContent()
      ) : (
        <div className={`${classPrefix}__placeholder`} />
      )}
    </div>
  )
}

MediaRenderer.propTypes = {
  url: string.isRequired,
  fallbackUrl: string,
  file: object,
  type: oneOf(['image', 'video']).isRequired,
  classPrefix: string,
  containerClass: string,
  externalId: string,
  source: oneOf(['youtube', 'vimeo', 'tiktok', 'x', 'upload', 'other']),
  isGalleryView: bool,
}

export default MediaRenderer
